/* ============================================================================
   MULTI-SPECIES PROCEDURAL VOXEL SHARK

   Every shark is a pure function of (species, seed, age). Body profiles, fin
   outlines, jaws, markings and swim gait are all parameters — nothing is a
   stored mesh — so a shark can be stored as a short spec and rebuilt exactly.
   That is what lets a visitor's shark cross a network as a dozen bytes.

   Vendored with three edits, all noted where they occur:
     - THREE is not imported; colours are written as plain floats, so this can
       run in a worker or in Node.
     - the auto-generated NAME is gone: a player names their own shark.
     - the tail-beat rate is gone: the game's movement model owns that, and a
       shark that carries its own swim speed would fight it.
   Everything else is upstream, including the comments.
   ============================================================================ */
'use strict';

const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const lerp = (a, b, t) => a + (b - a) * t;
function smoothstep(e0, e1, x) { const t = clamp((x - e0) / (e1 - e0), 0, 1); return t * t * (3 - 2 * t); }
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function makeNoise(seed) {
  const rnd = mulberry32(seed >>> 0), P = new Float32Array(512);
  for (let i = 0; i < 512; i++) P[i] = rnd();
  function h2(x, y) { let n = (x * 374761393 + y * 668265263) | 0; n = ((n ^ (n >> 13)) * 1274126177) | 0; return P[(n >>> 0) & 511]; }
  return function (x, y) {
    const xi = Math.floor(x), yi = Math.floor(y), xf = x - xi, yf = y - yi;
    const u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf);
    return lerp(lerp(h2(xi, yi), h2(xi + 1, yi), u), lerp(h2(xi, yi + 1), h2(xi + 1, yi + 1), u), v);
  };
}
function tableAt(ts, vs, t) {
  if (t <= ts[0]) return vs[0];
  const n = ts.length; if (t >= ts[n - 1]) return vs[n - 1];
  let i = 0; while (ts[i + 1] < t) i++;
  const f = (t - ts[i]) / (ts[i + 1] - ts[i]);
  return lerp(vs[i], vs[i + 1], f * f * (3 - 2 * f));
}
function inPoly(poly, x, y) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i][0], yi = poly[i][1], xj = poly[j][0], yj = poly[j][1];
    if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) inside = !inside;
  }
  return inside;
}
function polyBounds(p) {
  let a = 1e9, b = -1e9, c = 1e9, d = -1e9;
  for (const q of p) { if (q[0] < a) a = q[0]; if (q[0] > b) b = q[0]; if (q[1] < c) c = q[1]; if (q[1] > d) d = q[1]; }
  return [a, b, c, d];
}

const K_BODY = 0, K_FIN_V = 1, K_FIN_TOP = 2, K_FIN_BOT = 3, K_EYE = 4, K_RING = 5, K_GILL = 6, K_NARE = 7, K_BARB = 8;
const G_BODY = 1, G_LOWER = 2, G_UPPER = 3;
const BODY_END = 0.945;

const T16 = [0.000, 0.018, 0.042, 0.075, 0.115, 0.165, 0.230, 0.320, 0.420, 0.520, 0.620, 0.720, 0.800, 0.870, 0.910, 0.945];

const SPECIES = {
  tiger: {
    name: 'Tiger shark', latin: 'Galeocerdo cuvier', code: 'TGR', metres: 4.2,
    prof: {
      t: T16,
      w: [0.013, 0.027, 0.045, 0.065, 0.082, 0.092, 0.098, 0.098, 0.090, 0.078, 0.062, 0.045, 0.032, 0.022, 0.017, 0.014],
      h: [0.011, 0.023, 0.039, 0.057, 0.072, 0.083, 0.097, 0.104, 0.100, 0.090, 0.076, 0.058, 0.044, 0.034, 0.030, 0.028],
      cy: [0.013, 0.012, 0.011, 0.009, 0.006, 0.005, 0.004, 0.002, 0.000, 0.001, 0.003, 0.006, 0.010, 0.014, 0.017, 0.019]
    },
    nose: { a: 2.15, b: 0.60, f0: 0.30, f1: 0.02 },
    mouth: { x0: 0.052, hinge: 0.205, front: 1.05, back: 0.10, pow: 1.25, snout: 0.190 },
    eye: { x: 0.115, up: 0.30, side: 0.86, r: 0.0135 },
    gills: { n: 5, x0: 0.216, x1: 0.278, hi: 0.42, lo: 0.62, edge: 0.60 },
    fins: {
      d1: { x0: 0.412, x1: 0.562, h: 0.122, apex: 0.34, sweep: 0.012, rear: 0.075, th: 0.017, round: 0 },
      d2: { x0: 0.730, x1: 0.792, h: 0.050, apex: 0.36, sweep: 0.008, rear: 0.04, th: 0.010, round: 0 },
      an: { x0: 0.756, x1: 0.812, h: 0.050, apex: 0.36, sweep: 0.006, rear: 0.03, th: 0.010, round: 0 },
      pe: { x0: 0.235, x1: 0.322, span: 0.145, sweep: 0.040, drop: 0.56, dih: -0.36, th: 0.016 },
      pv: { x0: 0.612, x1: 0.664, span: 0.066, sweep: 0.026, drop: 0.62, dih: -0.46, th: 0.011 },
      cd: { x0: 0.860, up: 0.300, upLen: 0.292, lo: 0.074, loLen: 0.156, fork: 0.3, cw: 0.4, th: 0.015 }
    },
    pal: { d: [0.278, 0.341, 0.373], f: [0.545, 0.592, 0.608], b: [0.906, 0.886, 0.827], m: [0.121, 0.161, 0.192] },
    sh: { line: -0.30, soft: 0.28, top: 0.42, soft2: 0.30, rag: 0.00 },
    pattern: 'bars', bite: 'snap',
    swim: { waves: 0.85, r0: 0.08, rl: 0.80, base: 0.04, sharp: 2.0, amp: 0.055, chase: 0.085 }
  },
  white: {
    name: 'Great white', latin: 'Carcharodon carcharias', code: 'GWS', metres: 4.6,
    prof: {
      t: T16,
      w: [0.018, 0.038, 0.066, 0.092, 0.111, 0.122, 0.129, 0.129, 0.122, 0.106, 0.083, 0.058, 0.037, 0.025, 0.020, 0.016],
      h: [0.013, 0.030, 0.054, 0.080, 0.102, 0.122, 0.136, 0.140, 0.134, 0.120, 0.097, 0.070, 0.048, 0.037, 0.034, 0.032],
      cy: [0.020, 0.019, 0.017, 0.013, 0.010, 0.007, 0.004, 0.001, 0.000, 0.001, 0.003, 0.006, 0.009, 0.012, 0.014, 0.016]
    },
    nose: { a: 2.10, b: 0.35, f0: 0.26, f1: 0.02 },
    mouth: { x0: 0.058, hinge: 0.196, front: 1.05, back: 0.34, pow: 1.60, side: 0.05, snout: 0.180 },
    eye: { x: 0.108, up: 0.30, side: 0.88, r: 0.0125 },
    gills: { n: 5, x0: 0.204, x1: 0.286, hi: 0.56, lo: 0.86, edge: 0.55 },
    fins: {
      d1: { x0: 0.392, x1: 0.548, h: 0.170, apex: 0.35, sweep: 0.006, rear: 0.044, th: 0.022, round: 0 },
      d2: { x0: 0.736, x1: 0.780, h: 0.034, apex: 0.4, sweep: 0.003, rear: 0.02, th: 0.009, round: 0 },
      an: { x0: 0.762, x1: 0.806, h: 0.036, apex: 0.4, sweep: 0.003, rear: 0.018, th: 0.009, round: 0 },
      pe: { x0: 0.222, x1: 0.316, span: 0.196, sweep: 0.078, drop: 0.62, dih: -0.48, th: 0.019 },
      pv: { x0: 0.600, x1: 0.652, span: 0.065, sweep: 0.020, drop: 0.64, dih: -0.46, th: 0.011 },
      cd: { x0: 0.855, up: 0.268, upLen: 0.238, lo: 0.222, loLen: 0.200, fork: 0.55, cw: 0.52, th: 0.018 },
      kl: { x0: 0.856, x1: 0.945, span: 0.018, drop: 0.02, dih: 0, th: 0.009 }
    },
    pal: { d: [0.352, 0.372, 0.392], f: [0.470, 0.492, 0.510], b: [0.945, 0.935, 0.905], m: [0.300, 0.318, 0.336] },
    sh: { line: -0.13, soft: 0.10, top: 0.58, soft2: 0.34, rag: 0.23, head: -0.42 },
    pattern: 'plain', bite: 'snap',
    swim: { waves: 0.68, r0: 0.30, rl: 0.70, base: 0.03, sharp: 2.3, amp: 0.050, chase: 0.082 }
  },
  mako: {
    name: 'Shortfin mako', latin: 'Isurus oxyrinchus', code: 'MKO', metres: 3.2,
    prof: {
      t: T16,
      w: [0.008, 0.020, 0.037, 0.057, 0.073, 0.084, 0.090, 0.088, 0.078, 0.066, 0.052, 0.038, 0.026, 0.020, 0.017, 0.015],
      h: [0.007, 0.019, 0.038, 0.059, 0.077, 0.089, 0.096, 0.094, 0.086, 0.074, 0.058, 0.044, 0.032, 0.028, 0.028, 0.028],
      cy: [0.006, 0.006, 0.005, 0.004, 0.003, 0.002, 0.001, 0.000, 0.001, 0.002, 0.004, 0.007, 0.010, 0.012, 0.013, 0.014]
    },
    nose: { a: 2.05, b: 0.30, f0: 0.24, f1: 0.02 },
    mouth: { x0: 0.075, hinge: 0.186, front: 1.05, back: 0.18, pow: 1.40, snout: 0.172 },
    eye: { x: 0.095, up: 0.30, side: 0.90, r: 0.018 },
    gills: { n: 5, x0: 0.200, x1: 0.262, hi: 0.44, lo: 0.62, edge: 0.60 },
    fins: {
      d1: { x0: 0.400, x1: 0.512, h: 0.100, apex: 0.36, sweep: 0.005, rear: 0.03, th: 0.014, round: 0 },
      d2: { x0: 0.746, x1: 0.784, h: 0.022, apex: 0.4, sweep: 0, rear: 0.014, th: 0.007, round: 0 },
      an: { x0: 0.768, x1: 0.806, h: 0.024, apex: 0.4, sweep: 0, rear: 0.014, th: 0.007, round: 0 },
      pe: { x0: 0.235, x1: 0.302, span: 0.140, sweep: 0.070, drop: 0.58, dih: -0.26, th: 0.013 },
      pv: { x0: 0.615, x1: 0.658, span: 0.050, sweep: 0.018, drop: 0.62, dih: -0.44, th: 0.009 },
      cd: { x0: 0.870, up: 0.175, upLen: 0.150, lo: 0.160, loLen: 0.140, fork: 0.58, cw: 0.55, th: 0.014 },
      kl: { x0: 0.868, x1: 0.945, span: 0.018, drop: 0.02, dih: 0, th: 0.008 }
    },
    pal: { d: [0.118, 0.243, 0.396], f: [0.353, 0.518, 0.667], b: [0.964, 0.964, 0.949], m: [0.094, 0.196, 0.333] },
    sh: { line: -0.22, soft: 0.15, top: 0.34, soft2: 0.26, rag: 0.05 },
    pattern: 'plain', bite: 'snap',
    swim: { waves: 0.60, r0: 0.42, rl: 0.58, base: 0.02, sharp: 2.6, amp: 0.048, chase: 0.086 }
  },
  nurse: {
    name: 'Nurse shark', latin: 'Ginglymostoma cirratum', code: 'NRS', metres: 3.0,
    prof: {
      t: T16,
      w: [0.030, 0.052, 0.070, 0.084, 0.092, 0.096, 0.096, 0.092, 0.086, 0.076, 0.062, 0.048, 0.036, 0.028, 0.024, 0.022],
      h: [0.024, 0.042, 0.058, 0.070, 0.078, 0.084, 0.088, 0.088, 0.086, 0.080, 0.070, 0.058, 0.046, 0.038, 0.034, 0.032],
      cy: [0.004, 0.004, 0.003, 0.003, 0.002, 0.002, 0.001, 0.000, 0.000, 0.001, 0.002, 0.004, 0.006, 0.008, 0.010, 0.012]
    },
    nose: { a: 2.30, b: 1.10, f0: 0.24, f1: 0.01 },
    mouth: { x0: 0.022, hinge: 0.118, front: 0.86, back: 0.28, pow: 1.00, snout: 0.108 },
    eye: { x: 0.105, up: 0.36, side: 0.90, r: 0.008 },
    gills: { n: 5, x0: 0.152, x1: 0.208, hi: 0.36, lo: 0.54, edge: 0.62 },
    barbel: { x: 0.032, z: 0.030, r: 0.0075, len: 0.055 },
    fins: {
      d1: { x0: 0.545, x1: 0.644, h: 0.072, apex: 0.4, sweep: 0.005, rear: 0.03, th: 0.014, round: 0.30 },
      d2: { x0: 0.700, x1: 0.784, h: 0.058, apex: 0.4, sweep: 0.005, rear: 0.028, th: 0.013, round: 0.30 },
      an: { x0: 0.792, x1: 0.858, h: 0.046, apex: 0.4, sweep: 0.004, rear: 0.022, th: 0.011, round: 0.30 },
      pe: { x0: 0.220, x1: 0.332, span: 0.135, sweep: 0.010, drop: 0.55, dih: -0.28, th: 0.016 },
      pv: { x0: 0.560, x1: 0.636, span: 0.078, sweep: 0.008, drop: 0.60, dih: -0.42, th: 0.013 },
      cd: { x0: 0.880, up: 0.190, upLen: 0.290, lo: 0.044, loLen: 0.088, fork: 0.2, cw: 0.3, th: 0.014 }
    },
    pal: { d: [0.470, 0.372, 0.255], f: [0.600, 0.494, 0.348], b: [0.800, 0.722, 0.588], m: [0.318, 0.242, 0.160] },
    sh: { line: -0.45, soft: 0.40, top: 0.55, soft2: 0.45, rag: 0.06 },
    pattern: 'mottle', bite: 'suck',
    swim: { waves: 1.35, r0: 0.02, rl: 0.92, base: 0.18, sharp: 1.6, amp: 0.060, chase: 0.092 }
  },
  blacktip: {
    name: 'Blacktip reef', latin: 'Carcharhinus melanopterus', code: 'BTR', metres: 1.6,
    prof: {
      t: T16,
      w: [0.015, 0.031, 0.051, 0.069, 0.082, 0.089, 0.092, 0.088, 0.078, 0.066, 0.050, 0.036, 0.026, 0.020, 0.016, 0.014],
      h: [0.013, 0.028, 0.048, 0.066, 0.078, 0.087, 0.092, 0.090, 0.082, 0.070, 0.056, 0.042, 0.032, 0.026, 0.024, 0.024],
      cy: [0.010, 0.009, 0.008, 0.006, 0.005, 0.004, 0.002, 0.000, 0.001, 0.002, 0.004, 0.007, 0.010, 0.013, 0.015, 0.017]
    },
    nose: { a: 2.20, b: 0.55, f0: 0.26, f1: 0.02 },
    mouth: { x0: 0.048, hinge: 0.186, front: 1.05, back: 0.14, pow: 1.30, snout: 0.172 },
    eye: { x: 0.100, up: 0.30, side: 0.88, r: 0.012 },
    gills: { n: 5, x0: 0.198, x1: 0.256, hi: 0.40, lo: 0.58, edge: 0.60 },
    fins: {
      d1: { x0: 0.400, x1: 0.522, h: 0.105, apex: 0.32, sweep: 0.010, rear: 0.06, th: 0.014, round: 0 },
      d2: { x0: 0.700, x1: 0.764, h: 0.048, apex: 0.36, sweep: 0.006, rear: 0.034, th: 0.010, round: 0 },
      an: { x0: 0.726, x1: 0.786, h: 0.044, apex: 0.36, sweep: 0.004, rear: 0.028, th: 0.009, round: 0 },
      pe: { x0: 0.225, x1: 0.306, span: 0.140, sweep: 0.050, drop: 0.56, dih: -0.34, th: 0.014 },
      pv: { x0: 0.580, x1: 0.632, span: 0.058, sweep: 0.020, drop: 0.62, dih: -0.46, th: 0.010 },
      cd: { x0: 0.860, up: 0.245, upLen: 0.250, lo: 0.090, loLen: 0.150, fork: 0.32, cw: 0.4, th: 0.014 }
    },
    pal: { d: [0.545, 0.435, 0.290], f: [0.705, 0.612, 0.455], b: [0.940, 0.920, 0.858], m: [0.400, 0.318, 0.212] },
    sh: { line: -0.28, soft: 0.20, top: 0.40, soft2: 0.30, rag: 0.07 },
    pattern: 'plain', bite: 'snap', tip: { at: 0.50, col: [0.045, 0.055, 0.075] },
    swim: { waves: 0.90, r0: 0.10, rl: 0.80, base: 0.05, sharp: 2.0, amp: 0.058, chase: 0.092 }
  },
  hammer: {
    name: 'Great hammerhead', latin: 'Sphyrna mokarran', code: 'HMR', metres: 4.8,
    prof: {
      t: [0.000, 0.018, 0.038, 0.058, 0.082, 0.120, 0.175, 0.240, 0.330, 0.430, 0.530, 0.630, 0.730, 0.820, 0.890, 0.945],
      w: [0.158, 0.172, 0.168, 0.140, 0.108, 0.092, 0.094, 0.098, 0.096, 0.086, 0.072, 0.056, 0.040, 0.026, 0.019, 0.015],
      h: [0.020, 0.024, 0.030, 0.040, 0.056, 0.076, 0.090, 0.100, 0.100, 0.092, 0.078, 0.060, 0.044, 0.032, 0.028, 0.028],
      cy: [0.006, 0.006, 0.005, 0.005, 0.004, 0.003, 0.002, 0.001, 0.000, 0.001, 0.002, 0.005, 0.008, 0.012, 0.015, 0.017]
    },
    nose: { a: 2.15, b: 2.60, f0: 0.085, f1: 0.008 },
    headNotch: { x: 0.014, z: 0.024 },
    mouth: { x0: 0.058, hinge: 0.140, front: 1.02, back: 0.16, pow: 1.20, snout: 0.128 },
    eye: { x: 0.012, up: 0.00, side: 0.96, r: 0.014 },
    gills: { n: 5, x0: 0.190, x1: 0.252, hi: 0.42, lo: 0.60, edge: 0.60 },
    fins: {
      d1: { x0: 0.360, x1: 0.472, h: 0.165, apex: 0.24, sweep: 0.004, rear: 0.09, th: 0.014, round: 0 },
      d2: { x0: 0.722, x1: 0.792, h: 0.038, apex: 0.34, sweep: 0, rear: 0.046, th: 0.009, round: 0 },
      an: { x0: 0.700, x1: 0.776, h: 0.042, apex: 0.36, sweep: 0, rear: 0.04, th: 0.009, round: 0 },
      pe: { x0: 0.230, x1: 0.292, span: 0.110, sweep: 0.045, drop: 0.58, dih: -0.34, th: 0.012 },
      pv: { x0: 0.560, x1: 0.618, span: 0.062, sweep: 0.016, drop: 0.62, dih: -0.46, th: 0.010 },
      cd: { x0: 0.855, up: 0.270, upLen: 0.280, lo: 0.085, loLen: 0.150, fork: 0.3, cw: 0.38, th: 0.015 }
    },
    pal: { d: [0.427, 0.470, 0.443], f: [0.596, 0.635, 0.612], b: [0.925, 0.910, 0.855], m: [0.330, 0.372, 0.348] },
    sh: { line: -0.32, soft: 0.26, top: 0.44, soft2: 0.32, rag: 0.08 },
    pattern: 'plain', bite: 'snap',
    swim: { waves: 0.95, r0: 0.05, rl: 0.85, base: 0.14, sharp: 1.9, amp: 0.058, chase: 0.090 }
  },
  thresher: {
    name: 'Thresher shark', latin: 'Alopias vulpinus', code: 'THR', metres: 4.5,
    prof: {
      t: T16,
      w: [0.012, 0.026, 0.045, 0.063, 0.076, 0.084, 0.088, 0.086, 0.078, 0.066, 0.052, 0.038, 0.026, 0.020, 0.016, 0.014],
      h: [0.011, 0.025, 0.045, 0.065, 0.079, 0.090, 0.096, 0.094, 0.086, 0.074, 0.060, 0.046, 0.034, 0.028, 0.026, 0.026],
      cy: [0.008, 0.008, 0.007, 0.005, 0.004, 0.003, 0.001, 0.000, 0.001, 0.002, 0.004, 0.006, 0.009, 0.011, 0.013, 0.014]
    },
    nose: { a: 2.10, b: 0.45, f0: 0.26, f1: 0.02 },
    mouth: { x0: 0.060, hinge: 0.172, front: 1.05, back: 0.22, pow: 1.40, snout: 0.158 },
    eye: { x: 0.090, up: 0.30, side: 0.90, r: 0.018 },
    gills: { n: 5, x0: 0.186, x1: 0.244, hi: 0.40, lo: 0.58, edge: 0.60 },
    fins: {
      d1: { x0: 0.380, x1: 0.482, h: 0.098, apex: 0.34, sweep: 0.006, rear: 0.038, th: 0.014, round: 0 },
      d2: { x0: 0.742, x1: 0.778, h: 0.018, apex: 0.42, sweep: 0, rear: 0.01, th: 0.006, round: 0 },
      an: { x0: 0.762, x1: 0.798, h: 0.020, apex: 0.42, sweep: 0, rear: 0.01, th: 0.006, round: 0 },
      pe: { x0: 0.220, x1: 0.288, span: 0.180, sweep: 0.085, drop: 0.56, dih: -0.24, th: 0.013 },
      pv: { x0: 0.600, x1: 0.648, span: 0.052, sweep: 0.016, drop: 0.62, dih: -0.44, th: 0.009 },
      cd: { x0: 0.850, up: 0.430, upLen: 0.620, lo: 0.086, loLen: 0.140, fork: 0.16, cw: 0.24, th: 0.016 }
    },
    pal: { d: [0.216, 0.255, 0.325], f: [0.412, 0.463, 0.533], b: [0.905, 0.895, 0.860], m: [0.160, 0.192, 0.255] },
    sh: { line: -0.20, soft: 0.16, top: 0.40, soft2: 0.28, rag: 0.08 },
    pattern: 'plain', bite: 'snap',
    swim: { waves: 0.80, r0: 0.15, rl: 0.78, base: 0.03, sharp: 2.2, amp: 0.050, chase: 0.080 }
  },
  whale: {
    name: 'Whale shark', latin: 'Rhincodon typus', code: 'WHL', metres: 9.5,
    prof: {
      t: T16,
      w: [0.104, 0.113, 0.121, 0.128, 0.133, 0.135, 0.134, 0.126, 0.112, 0.095, 0.074, 0.052, 0.035, 0.025, 0.020, 0.017],
      h: [0.038, 0.046, 0.060, 0.078, 0.096, 0.112, 0.124, 0.128, 0.122, 0.108, 0.088, 0.065, 0.048, 0.038, 0.036, 0.034],
      cy: [0.006, 0.006, 0.005, 0.004, 0.003, 0.002, 0.001, 0.000, 0.001, 0.002, 0.004, 0.008, 0.012, 0.016, 0.019, 0.021]
    },
    nose: { a: 2.30, b: 1.95, f0: 0.26, f1: 0.01 },
    mouth: { x0: 0.004, hinge: 0.106, front: 0.42, back: 0.30, pow: 1.00, snout: 0.096 },
    eye: { x: 0.034, up: -0.06, side: 0.95, r: 0.010 },
    gills: { n: 5, x0: 0.150, x1: 0.252, hi: 0.58, lo: 0.80, edge: 0.52 },
    fins: {
      d1: { x0: 0.520, x1: 0.642, h: 0.130, apex: 0.36, sweep: 0.008, rear: 0.045, th: 0.020, round: 0 },
      d2: { x0: 0.762, x1: 0.816, h: 0.042, apex: 0.4, sweep: 0.004, rear: 0.018, th: 0.010, round: 0 },
      an: { x0: 0.776, x1: 0.832, h: 0.040, apex: 0.4, sweep: 0.004, rear: 0.016, th: 0.010, round: 0 },
      pe: { x0: 0.220, x1: 0.312, span: 0.190, sweep: 0.075, drop: 0.56, dih: -0.30, th: 0.020 },
      pv: { x0: 0.620, x1: 0.682, span: 0.070, sweep: 0.018, drop: 0.62, dih: -0.44, th: 0.013 },
      cd: { x0: 0.870, up: 0.290, upLen: 0.230, lo: 0.185, loLen: 0.170, fork: 0.45, cw: 0.46, th: 0.016 },
      kl: { x0: 0.260, x1: 0.900, span: 0.017, drop: 0.02, dih: 0, th: 0.011 },
      kl2: { x0: 0.215, x1: 0.720, span: 0.014, drop: -0.34, dih: 0, th: 0.009 },
      kl3: { x0: 0.300, x1: 0.740, span: 0.013, drop: 0.36, dih: 0, th: 0.009 }
    },
    pal: { d: [0.145, 0.255, 0.310], f: [0.216, 0.345, 0.400], b: [0.906, 0.890, 0.820], m: [0.792, 0.886, 0.910] },
    sh: { line: -0.50, soft: 0.16, top: 0.40, soft2: 0.40, rag: 0.05 },
    pattern: 'spots', bite: 'gulp',
    swim: { waves: 1.05, r0: 0.10, rl: 0.85, base: 0.10, sharp: 1.8, amp: 0.045, chase: 0.066 }
  }
};
const SP_KEYS = Object.keys(SPECIES);

/* ============================================================================
   FIN OUTLINE SYNTHESIS
   ========================================================================== */
function tab(P, k, x) { return tableAt(P.t, P[k], clamp(x, 0, BODY_END)); }

function vFinPoly(P, f, up, scale) {
  /* Shark dorsal profile: steep convex leading edge, apex forward of centre,
     long concave trailing edge running back to a free rear tip. */
  const baseAt = x => { const q = clamp(x, 0, BODY_END); return tab(P, 'cy', q) + up * tab(P, 'h', q) * 0.86; };
  const W = f.x1 - f.x0;
  const ax = f.x0 + W * f.apex + f.sweep;
  const tipx = f.x1 + f.rear;
  const b0 = baseAt(ax), H = up * f.h * scale;
  const le = ax - f.x0, te = tipx - ax, rd = f.round || 0;
  const pts = [[f.x0, baseAt(f.x0)],
  [f.x0 + le * 0.30, b0 + H * 0.45],
  [f.x0 + le * 0.68, b0 + H * 0.80]];
  if (rd > 0) {
    pts.push([ax - rd * W * 0.45, b0 + H * 0.96]);
    pts.push([ax + rd * W * 0.45, b0 + H * 0.96]);
  } else pts.push([ax, b0 + H]);
  pts.push([ax + te * 0.10, b0 + H * 0.74]);
  pts.push([ax + te * 0.28, b0 + H * 0.45]);
  pts.push([ax + te * 0.58, b0 + H * 0.26]);
  pts.push([tipx, baseAt(tipx) + up * 0.004]);   /* free rear tip runs out into the back */
  for (let i = 5; i >= 1; i--) {                 /* root follows the body so the fin never floats */
    const x = f.x0 + (tipx - f.x0) * (i / 6);
    pts.push([x, baseAt(x) - up * 0.004]);
  }
  return pts;
}
function caudalPoly(P, c, scale) {
  /* Upper lobe: dorsal margin plus a trailing edge held a real chord away from
     it, so long lobes stay blades rather than ribbons. Lower lobe hangs off a
     fork set well out from the body. */
  const x0 = c.x0, cy0 = tab(P, 'cy', x0), bh = tab(P, 'h', x0) * 0.92;
  const U = c.upLen * scale, V = c.up * scale, L = c.loLen * scale, W = c.lo * scale;
  const cw = (c.cw !== undefined ? c.cw : 0.42) * U;
  const fk = c.fork !== undefined ? c.fork : 0.30;
  const fx = fk * Math.min(U, L * 1.9), fy = (V - W) * 0.5 * fk * 0.30;
  const LE = [[0, bh], [0.28 * U, 0.40 * V], [0.66 * U, 0.77 * V], [U, V]];
  const leX = ty => {
    const y = ty * V;
    if (y <= LE[0][1]) return 0;
    for (let i = 0; i < 3; i++) {
      if (y <= LE[i + 1][1]) {
        const t = (y - LE[i][1]) / ((LE[i + 1][1] - LE[i][1]) || 1e-9);
        return LE[i][0] + (LE[i + 1][0] - LE[i][0]) * t;
      }
    }
    return U;
  };
  const te = (ty, ex) => [x0 + leX(ty) + cw * Math.pow(1 - ty, 0.8) + (ex || 0), cy0 + ty * V];
  const low = a => { const q = clamp(x0 + a, 0, BODY_END); return tab(P, 'cy', q) - tab(P, 'h', q) * 0.92 - cy0; };
  const p = (a, b) => [x0 + a, cy0 + b];
  return [p(0, bh),
  p(LE[1][0], LE[1][1]), p(LE[2][0], LE[2][1]), p(U, V),
  te(0.95), te(0.80), te(0.74, 0.09 * U), te(0.68), te(0.46),
  p(fx, fy),
  p(fx + (L - fx) * 0.55, fy - (W + fy) * 0.55),
  p(L, -W),
  p(0.82 * L, -0.90 * W),
  p(0.40 * L, -0.52 * W),
  p(0.14 * L, low(0.14 * L)), p(0, -bh)];
}
function hFinPoly(P, f, scale) {
  const x0 = f.x0, x1 = f.x1, dx = x1 - x0, S = f.span * scale, sb = f.sweep * scale;
  const r0 = tab(P, 'w', (x0 + x1) * 0.5) * 0.42;
  const tf = x0 + sb + dx * 0.18, tb = tf + dx * 0.30;
  return [[x0, r0],
  [x0 + (tf - x0) * 0.40, r0 + S * 0.48],
  [tf, r0 + S],
  [tb, r0 + S * 0.90],
  [x0 + dx * 0.88 + sb * 0.38, r0 + S * 0.38],
  [x1, r0 * 0.96]];
}
function ridgePoly(P, f, scale) {
  const S = f.span * scale, r0 = tab(P, 'w', (f.x0 + f.x1) * 0.5) * 0.40;
  return [[f.x0, r0], [f.x0 + (f.x1 - f.x0) * 0.22, r0 + S], [f.x1 - (f.x1 - f.x0) * 0.06, r0 + S * 0.9], [f.x1, r0 * 0.95]];
}

function makeFins(sp, rnd, minTh) {
  const P = sp.prof, F = sp.fins, out = [];
  const mt = minTh || 0.002;
  const dS = 0.90 + 0.22 * rnd(), pS = 0.92 + 0.18 * rnd(), tS = 0.94 + 0.14 * rnd();
  const V = (poly, thick, ay0, ay1, taper, radial) => ({
    plane: 'V', poly: poly, b: polyBounds(poly), thick: thick,
    th: (x, y) => {
      let t;
      if (radial) { const d = Math.hypot(x - radial[0], (y - radial[1]) * 0.95); t = thick * (1 - taper * clamp(d / radial[2], 0, 1)); }
      else t = thick * (1 - taper * clamp((y - ay0) / (ay1 - ay0), 0, 1));
      return t < mt ? mt : t;
    },
    tf: (x, y) => radial ? clamp(Math.hypot(x - radial[0], (y - radial[1]) * 0.95) / radial[2], 0, 1) : clamp((y - ay0) / (ay1 - ay0), 0, 1)
  });
  const H = (poly, thick, drop, dih, taper) => {
    const b = polyBounds(poly);
    return {
      plane: 'H', poly: poly, b: b, thick: thick, drop: drop, dih: dih,
      th: (az) => { const t = thick * (1 - taper * clamp((az - b[2]) / (b[3] - b[2]), 0, 1)); return t < mt ? mt : t; },
      tf: (az) => clamp((az - b[2]) / (b[3] - b[2]), 0, 1)
    };
  };

  if (F.d1) { const p = vFinPoly(P, F.d1, 1, dS), bb = polyBounds(p); out.push(V(p, F.d1.th, bb[2], bb[3], 0.74)); }
  if (F.d2) { const p = vFinPoly(P, F.d2, 1, 1), bb = polyBounds(p); out.push(V(p, F.d2.th, bb[2], bb[3], 0.70)); }
  if (F.an) { const p = vFinPoly(P, F.an, -1, 1), bb = polyBounds(p); out.push(V(p, F.an.th, bb[3], bb[2], 0.66)); }
  if (F.cd) {
    const p = caudalPoly(P, F.cd, tS), cy0 = tab(P, 'cy', F.cd.x0);
    out.push(V(p, F.cd.th, 0, 0, 0.76, [F.cd.x0 + 0.035, cy0, Math.max(F.cd.upLen, F.cd.loLen) * tS * 1.05]));
  }
  if (F.pe) { const p = hFinPoly(P, F.pe, pS); out.push(H(p, F.pe.th, F.pe.drop, F.pe.dih, 0.62)); }
  if (F.pv) { const p = hFinPoly(P, F.pv, 1); out.push(H(p, F.pv.th, F.pv.drop, F.pv.dih, 0.55)); }
  for (const rk of ['kl', 'kl2', 'kl3']) if (F[rk]) { const q = ridgePoly(P, F[rk], 1); out.push(H(q, F[rk].th, F[rk].drop, F[rk].dih, 0.35)); }
  return out;
}

/* ============================================================================
   VOXELISER
   ========================================================================== */
function buildShark(spKey, res, seed, ageParam) {
  const sp = SPECIES[spKey];
  const rnd = mulberry32(seed >>> 0), noise = makeNoise(seed * 7919 + 13);
  const FINS = makeFins(sp, rnd, 0.52 / res);

  const wMul = 0.94 + 0.13 * rnd(), hMul = 0.95 + 0.11 * rnd();
  const P = { t: sp.prof.t, w: sp.prof.w.map(v => v * wMul), h: sp.prof.h.map(v => v * hMul), cy: sp.prof.cy };

  /* bounds from profile + fins */
  let X0 = -0.03, X1 = BODY_END + 0.02, Y0 = 0, Y1 = 0, Z1 = 0;
  for (let i = 0; i < P.t.length; i++) {
    Y1 = Math.max(Y1, P.cy[i] + P.h[i]); Y0 = Math.min(Y0, P.cy[i] - P.h[i]); Z1 = Math.max(Z1, P.w[i]);
  }
  for (const f of FINS) {
    X0 = Math.min(X0, f.b[0]); X1 = Math.max(X1, f.b[1]);
    if (f.plane === 'V') { Y0 = Math.min(Y0, f.b[2]); Y1 = Math.max(Y1, f.b[3]); Z1 = Math.max(Z1, f.thick); }
    else {
      Z1 = Math.max(Z1, f.b[3]);
      for (let s = 0; s <= 6; s++) {
        const x = lerp(f.b[0], f.b[1], s / 6);
        const yc = tab(P, 'cy', x) - f.drop * tab(P, 'h', x) + f.dih * (f.b[3] - f.b[2]);
        Y0 = Math.min(Y0, yc - f.thick); Y1 = Math.max(Y1, yc + f.thick);
      }
    }
  }
  if (sp.barbel) Y0 = Math.min(Y0, tab(P, 'cy', sp.barbel.x) - tab(P, 'h', sp.barbel.x) * 0.85 - sp.barbel.len);
  const pad = 4 / res;
  X0 -= pad; X1 += pad; Y0 -= pad; Y1 += pad; Z1 += pad;

  const v = 1 / res;
  const kz = Math.ceil(Z1 * res);
  const Z0 = -(kz + 0.5) * v; Z1 = (kz + 0.5) * v;
  const NX = Math.ceil((X1 - X0) * res), NY = Math.ceil((Y1 - Y0) * res), NZ = 2 * kz + 1;
  const total = NX * NY * NZ;
  const grp = new Uint8Array(total), knd = new Uint8Array(total), tipA = new Uint8Array(total);
  const idx = (i, j, k) => (i * NY + j) * NZ + k;

  /* oral cavity derived from the mouth parameters */
  const M = sp.mouth;
  const ccx = (M.x0 + M.hinge) * 0.5, chh = tab(P, 'h', ccx), cww = tab(P, 'w', ccx);
  const cav = {
    x: ccx, rx: (M.hinge - M.x0) * 0.52, ry: Math.min(0.36 * chh, 0.40 * chh), rz: 0.70 * cww,
    y: tab(P, 'cy', ccx) - 0.18 * chh, z: 0
  };
  const cutY = (x, az, cy, h, w) => {
    const u = clamp((x - M.x0) / (M.hinge - M.x0), 0, 1);
    const sd = M.side !== undefined ? M.side : 0.16;
    return cy - h * (M.front - (M.front - M.back) * Math.pow(u, M.pow)) + h * sd * (az / Math.max(w, 1e-4)) * (az / Math.max(w, 1e-4));
  };

  const E = sp.eye, eW = tab(P, 'w', E.x), eH = tab(P, 'h', E.x), eCY = tab(P, 'cy', E.x);
  const eY = eCY + E.up * eH, eZ = E.side * eW, eR = E.r, eRing = E.r * 1.48;
  const G = sp.gills;

  /* ---- pass 1: fill ---- */
  for (let i = 0; i < NX; i++) {
    const x = X0 + (i + 0.5) * v;
    const inBody = (x >= 0 && x <= BODY_END);
    const w = inBody ? tab(P, 'w', x) : 0, h = inBody ? tab(P, 'h', x) : 0, cy = tab(P, 'cy', x);
    const n = sp.nose.a + sp.nose.b * smoothstep(sp.nose.f0, sp.nose.f1, x);
    const isGillX = (x > G.x0 && x < G.x1);
    const gillPh = (x - G.x0) / (G.x1 - G.x0) * G.n;

    for (let j = 0; j < NY; j++) {
      const y = Y0 + (j + 0.5) * v;
      for (let k = 0; k < NZ; k++) {
        const z = Z0 + (k + 0.5) * v, az = Math.abs(z);
        let kind = -1, tipf = 0;

        if (inBody && az <= w && Math.abs(y - cy) <= h) {
          let dz = az / w, dy = (y - cy) / h, nn = n;
          if (dy < 0) { dy /= 0.965; nn += 0.45; }
          if (Math.pow(dz, nn) + Math.pow(Math.abs(dy), nn) <= 1) kind = K_BODY;
        }
        if (kind < 0) {
          for (let f = 0; f < FINS.length; f++) {
            const F = FINS[f];
            if (F.plane === 'V') {
              if (x < F.b[0] || x > F.b[1] || y < F.b[2] || y > F.b[3]) continue;
              if (az <= F.th(x, y) && inPoly(F.poly, x, y)) { kind = K_FIN_V; tipf = F.tf(x, y); break; }
            } else {
              if (x < F.b[0] || x > F.b[1] || az < F.b[2] || az > F.b[3]) continue;
              const fx = clamp(x, 0, BODY_END);
              const yc = tab(P, 'cy', fx) - F.drop * tab(P, 'h', fx) + F.dih * (az - F.b[2]);
              const dy = y - yc;
              if (Math.abs(dy) <= F.th(az) && inPoly(F.poly, x, az)) { kind = dy >= 0 ? K_FIN_TOP : K_FIN_BOT; tipf = F.tf(az); break; }
            }
          }
        }
        if (kind < 0 && sp.barbel) {
          const B = sp.barbel, by = tab(P, 'cy', B.x) - tab(P, 'h', B.x) * 0.82;
          if (y < by && y > by - B.len) {
            const lean = (by - y) * 0.30;
            if (Math.abs(x - B.x - lean) < B.r && Math.abs(az - B.z) < B.r) kind = K_BARB;
          }
        }
        if (kind < 0) continue;

        if (sp.headNotch && x < sp.headNotch.x && az < sp.headNotch.z) continue;

        const cdx = (x - cav.x) / cav.rx, cdy = (y - cav.y) / cav.ry, cdz = (z - cav.z) / cav.rz;
        if (cdx * cdx + cdy * cdy + cdz * cdz < 1) continue;

        if (kind === K_BODY) {
          const dEye = Math.hypot(x - E.x, (y - eY) * 1.25, (az - eZ) * 0.9);
          if (dEye < eR) kind = K_EYE;
          else if (dEye < eRing) kind = K_RING;
          else if (isGillX && az > G.edge * w && y > cy - G.lo * h && y < cy + G.hi * h) {
            const fp = gillPh - Math.floor(gillPh);
            if (fp < 0.40) kind = K_GILL;
          } else if (!sp.headNotch && x > M.x0 * 0.55 && x < M.x0 * 1.15 && az > 0.22 * w && az < 0.52 * w && y < cy - 0.60 * h) kind = K_NARE;
        }

        let g = G_BODY;
        if (x < M.hinge && inBody) g = (y < cutY(x, az, cy, h, w)) ? G_LOWER : (x < M.snout ? G_UPPER : G_BODY);
        const id = idx(i, j, k);
        grp[id] = g; knd[id] = kind + 1; tipA[id] = (tipf * 255) | 0;
      }
    }
  }

  /* ---- pass 2: surface shell + jaw parting surface ---- */
  const px = [], py = [], pz = [], pg = [], col = [];
  const PAL = sp.pal, SH = sp.sh;

  for (let i = 0; i < NX; i++) {
    const x = X0 + (i + 0.5) * v, fx = clamp(x, 0, BODY_END);
    const cy = tab(P, 'cy', fx), h = tab(P, 'h', fx), w = tab(P, 'w', fx);
    for (let j = 0; j < NY; j++) {
      const y = Y0 + (j + 0.5) * v;
      for (let k = 0; k < NZ; k++) {
        const id = idx(i, j, k), g = grp[id];
        if (!g) continue;
        let outer = false, lining = false, cut = false;
        for (let d = 0; d < 6; d++) {
          const ii = i + (d === 0 ? 1 : d === 1 ? -1 : 0), jj = j + (d === 2 ? 1 : d === 3 ? -1 : 0), kk = k + (d === 4 ? 1 : d === 5 ? -1 : 0);
          if (ii < 0 || ii >= NX || jj < 0 || jj >= NY || kk < 0 || kk >= NZ) { outer = true; continue; }
          const ng = grp[idx(ii, jj, kk)];
          if (!ng) {
            const ax = (X0 + (ii + 0.5) * v - cav.x) / cav.rx, ay = (Y0 + (jj + 0.5) * v - cav.y) / cav.ry, az2 = (Z0 + (kk + 0.5) * v - cav.z) / cav.rz;
            if (ax * ax + ay * ay + az2 * az2 < 1) lining = true; else outer = true;
            continue;
          }
          if ((ng === G_LOWER) !== (g === G_LOWER)) cut = true;
        }
        if (!outer && !lining && !cut) continue;

        const z = Z0 + (k + 0.5) * v, az = Math.abs(z);
        const kind = knd[id] - 1, tipf = tipA[id] / 255;
        let r, gg, b;

        /* countershading, with an optionally ragged demarcation */
        const s = clamp((y - cy) / Math.max(h, 1e-4), -2.4, 2.4);
        let line = SH.line;
        if (SH.rag > 0) line += (noise(x * 13.0, 3.1) - 0.5) * SH.rag + (noise(x * 38.0, 6.2) - 0.5) * SH.rag * 0.45;
        if (SH.head) line += SH.head * smoothstep(0.32, 0.06, x);
        const mid = smoothstep(line - SH.soft, line + SH.soft, s);
        const top = smoothstep(SH.top - SH.soft2, SH.top + SH.soft2, s);
        r = lerp(lerp(PAL.b[0], PAL.f[0], mid), PAL.d[0], top);
        gg = lerp(lerp(PAL.b[1], PAL.f[1], mid), PAL.d[1], top);
        b = lerp(lerp(PAL.b[2], PAL.f[2], mid), PAL.d[2], top);

        if (kind === K_FIN_TOP) { r = PAL.d[0] * 0.94; gg = PAL.d[1] * 0.94; b = PAL.d[2] * 0.96; }
        else if (kind === K_FIN_BOT) { r = lerp(PAL.b[0], PAL.f[0], 0.30); gg = lerp(PAL.b[1], PAL.f[1], 0.30); b = lerp(PAL.b[2], PAL.f[2], 0.30); }
        else if (kind === K_FIN_V) { r *= 0.93; gg *= 0.94; b *= 0.97; }
        else if (kind === K_BARB) { r = lerp(PAL.b[0], PAL.f[0], 0.5); gg = lerp(PAL.b[1], PAL.f[1], 0.5); b = lerp(PAL.b[2], PAL.f[2], 0.5); }

        /* species markings */
        if (kind === K_BODY || kind === K_FIN_V || kind === K_FIN_TOP) {
          let m = 0;
          if (sp.pattern === 'bars') {
            const lean = (noise(x * 9.2, 4.3) - 0.5) * 3.2;
            const bars = Math.sin(x * 57.0 + noise(x * 2.2, 0.7) * 7.0 + lean * (s * 0.50 + s * s * 1.15));
            const wob = (noise(x * 22.0, s * 3.1 + 2.0) - 0.5) * 0.95;
            const brk = (noise(x * 7.0, s * 5.5 + 9.0) - 0.5) * 0.85 * smoothstep(0.28, 0.66, x);
            const thr = 0.32 + (noise(x * 8.6, 11.0) - 0.5) * 0.30;
            m = smoothstep(thr, thr + 0.22, bars + wob + brk);
            m *= smoothstep(-0.48, 0.02, s);
            m *= smoothstep(1.05, 0.72, x) * 0.80 + 0.20;
            m *= (1 - ageParam) * 0.94;
          } else if (sp.pattern === 'mottle') {
            const n1 = noise(x * 9.0, s * 5.0 + 3.0), n2 = noise(x * 24.0, s * 13.0 + 7.0);
            m = smoothstep(0.50, 0.74, n1 * 0.70 + n2 * 0.30) * 0.50;
            const sp1 = noise(x * 64.0, s * 34.0 + 11.0);
            m = Math.max(m, smoothstep(0.79, 0.90, sp1) * (1 - ageParam) * 0.85);
            m *= smoothstep(-0.85, -0.25, s);
          } else if (sp.pattern === 'spots') {
            const ang = Math.atan2((y - cy) / Math.max(h, 1e-4), z / Math.max(w, 1e-4));
            const gx = x * 30.0, gy = ang * 3.2;
            const ix = Math.round(gx), iy = Math.round(gy);
            const jx = (noise(ix * 3.1, iy * 2.7) - 0.5) * 0.55, jy = (noise(ix * 1.7 + 9, iy * 4.3) - 0.5) * 0.55;
            m = 1 - smoothstep(0.14, 0.26, Math.hypot(gx - ix - jx, gy - iy - jy));
            if (((ix % 5) + 5) % 5 === 0) m = Math.max(m, (1 - smoothstep(0.11, 0.20, Math.abs(gx - ix - jx * 0.3))) * 0.75);
            m *= smoothstep(-0.72, -0.18, s) * 0.92;
          }
          if (m > 0) { r = lerp(r, PAL.m[0], m); gg = lerp(gg, PAL.m[1], m); b = lerp(b, PAL.m[2], m); }
        }

        /* dark fin tips */
        if (sp.tip && (kind === K_FIN_V || kind === K_FIN_TOP || kind === K_FIN_BOT) && tipf > sp.tip.at) {
          const t = smoothstep(sp.tip.at, Math.min(1, sp.tip.at + 0.13), tipf);
          r = lerp(r, sp.tip.col[0], t); gg = lerp(gg, sp.tip.col[1], t); b = lerp(b, sp.tip.col[2], t);
        }

        if (kind === K_EYE) { r = 0.035; gg = 0.043; b = 0.055; }
        else if (kind === K_RING) { r *= 0.62; gg *= 0.66; b *= 0.70; }
        else if (kind === K_GILL) { r *= 0.46; gg *= 0.50; b *= 0.56; }
        else if (kind === K_NARE) { r *= 0.52; gg *= 0.55; b *= 0.60; }

        if (cut || lining) {
          const cdx = (x - cav.x) / cav.rx, cdy = (y - cav.y) / cav.ry, cdz = (z - cav.z) / cav.rz;
          const cr = cdx * cdx + cdy * cdy + cdz * cdz;
          const jawline = cut && cr < 2.15 && x > M.x0 * 0.85 && x < M.hinge * 1.02;
          const tooth = ((i + k) & 1) === 0;
          if (outer) {
            if (jawline && tooth) { r = 0.84; gg = 0.83; b = 0.78; }
            else if (cut) {
              const f = lerp(0.42, 1.0, smoothstep(2.15, 5.2, cr));
              r *= f; gg *= lerp(0.43, 1.0, smoothstep(2.15, 5.2, cr)); b *= lerp(0.48, 1.0, smoothstep(2.15, 5.2, cr));
            }
          } else if (jawline) {
            if (tooth) { r = 0.96; gg = 0.95; b = 0.90; } else { r = 0.60; gg = 0.35; b = 0.36; }
          } else if (cr < 3.6) { r = 0.46; gg = 0.235; b = 0.255; }
          else { r = 0.33; gg = 0.185; b = 0.205; }
        }

        const gr = 1 + (noise(x * 160.0, (y * 90.0 + z * 70.0)) - 0.5) * 0.10;
        // EDIT: plain floats, not a THREE.Color, so this runs in a worker and in Node.
        px.push(x); py.push(y); pz.push(z); pg.push(g);
        col.push(clamp(r * gr, 0, 1), clamp(gg * gr, 0, 1), clamp(b * gr, 0, 1));
      }
    }
  }
  return {
    x: new Float32Array(px), y: new Float32Array(py), z: new Float32Array(pz),
    g: new Uint8Array(pg), c: new Float32Array(col), n: px.length, vox: v,
    bounds: [X0, X1, Y0, Y1, Z0, Z1], sp: sp, key: spKey,
    hingeX: M.hinge, hingeY: tab(P, 'cy', M.hinge) - Math.max(0.12, M.back * 0.85) * tab(P, 'h', M.hinge),
    snoutX: M.snout, snoutY: tab(P, 'cy', M.snout), noseGap: M.x0
  };
}

/** a short shareable id for a build. NOT a name — the player supplies that. */
function shareCode(key, seed) {
  return SPECIES[key].code + '-' + (seed >>> 0).toString(36).toUpperCase().padStart(4, '0');
}

export { buildShark, shareCode, SPECIES, SP_KEYS, BODY_END, G_BODY, G_LOWER, G_UPPER };
export default { buildShark, shareCode, SPECIES, SP_KEYS };
